import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { documentsApi } from '@/lib/documents';
import { AppShell } from '@/components/app/AppShell';
import { EmptyState } from '@/components/app/EmptyState';
import { NextBestAction } from '@/components/app/NextBestAction';
import { WeeklyDigest } from '@/components/app/WeeklyDigest';
import { useDialogs } from '@/components/app/DialogProvider';

export function WorkspacePage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const { alert } = useDialogs();

  const { data: docs } = useQuery({
    queryKey: ['documents', wsId, 'summary'],
    queryFn: () => documentsApi.list(wsId!),
    enabled: !!wsId,
  });

  const seedDemo = useMutation({
    mutationFn: () => documentsApi.seedDemo(wsId!),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['documents', wsId] });
      qc.invalidateQueries({ queryKey: ['next-actions', wsId] });
      void alert({
        title: r.alreadyPresent
          ? (t('workspace.seed_already', { defaultValue: 'Sample documents are already loaded.' }) as string)
          : (t('workspace.seed_done', { defaultValue: 'Loaded {{n}} sample documents — indexing now. Try Ask in a moment.', n: r.seeded }) as string),
      });
    },
    onError: () => alert({ title: t('common.action_failed', { defaultValue: 'Something went wrong. Please try again.' }) as string }),
  });

  const indexed = docs?.filter((d) => d.status === 'indexed').length ?? 0;
  const total = docs?.length ?? 0;
  const failed = docs?.filter((d) => d.status === 'failed').length ?? 0;
  const pending = docs?.filter((d) =>
    ['pending', 'parsing', 'chunking', 'indexing'].includes(d.status),
  ).length ?? 0;

  return (
    <AppShell maxWidth="max-w-5xl">
      <section className="paper-spotlight rounded-2xl p-7 mb-7">
        <p className="text-[11px] uppercase tracking-[0.18em] text-accent-600 font-semibold">
          {t('workspace.eyebrow', { defaultValue: 'Knowledge workspace' })}
        </p>
        <h2 className="display serif-jp mt-2 text-3xl font-semibold tracking-tight text-ink-900 leading-tight text-balance">
          {t('workspace.hero', { defaultValue: 'Every document. Every signal. Every thought.' })}
        </h2>
        <p className="mt-2 text-[14px] text-ink-600 max-w-xl">
          {t('workspace.hero_sub', { defaultValue: 'One knowing plane for your team.' })}
        </p>
      </section>

      {wsId && <NextBestAction wsId={wsId} />}
      {wsId && total > 0 && <WeeklyDigest wsId={wsId} />}

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Stat label={t('workspace.stat.docs', { defaultValue: 'Documents' }) as string} value={total} to={`/app/workspace/${wsId}/docs`} />
        <Stat label={t('workspace.stat.indexed', { defaultValue: 'Indexed' }) as string} value={indexed} accent to={`/app/workspace/${wsId}/docs?status=indexed`} />
        <Stat label={t('workspace.stat.failed', { defaultValue: 'Failed' }) as string} value={failed} danger={failed > 0} to={`/app/workspace/${wsId}/docs?status=failed`} />
        <Stat label={t('workspace.stat.pending', { defaultValue: 'Pending' }) as string} value={pending} to={`/app/workspace/${wsId}/docs?status=pending`} />
      </section>

      {docs && total === 0 && (
        <section className="mb-8">
          <EmptyState
            title={t('workspace.empty.title', { defaultValue: 'No documents yet' }) as string}
            description={t('workspace.empty.sub', { defaultValue: 'Upload your first document or connect a source — Knowlez indexes it, then you can ask, search, and compose against it.' }) as string}
            actions={[
              { label: t('workspace.empty.upload', { defaultValue: 'Upload a document' }) as string, to: `/app/workspace/${wsId}/upload`, primary: true },
              {
                label: seedDemo.isPending
                  ? (t('workspace.seed_loading', { defaultValue: 'Loading sample…' }) as string)
                  : (t('workspace.seed_cta', { defaultValue: 'Load sample workspace' }) as string),
                onClick: () => seedDemo.mutate(),
                disabled: seedDemo.isPending,
              },
              { label: t('workspace.empty.connect', { defaultValue: 'Connect a source' }) as string, to: `/app/workspace/${wsId}/connectors` },
            ]}
          />
        </section>
      )}

      <section className="mb-8">
        <h3 className="text-[11px] uppercase tracking-[0.16em] text-ink-500 font-semibold mb-3">
          {t('workspace.quick_actions', { defaultValue: 'Quick actions' })}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <ActionCard
            to={`/app/workspace/${wsId}/ask`}
            label={t('nav.ask', { defaultValue: 'Ask' }) as string}
            body={t('workspace.quick.ask', { defaultValue: 'Ask any question of your workspace. Every answer is cited.' }) as string}
            badge="知"
          />
          <ActionCard
            to={`/app/workspace/${wsId}/upload`}
            label={t('upload.title', { defaultValue: 'Upload documents' }) as string}
            body={t('workspace.quick.upload', { defaultValue: 'Drop PDFs, decks, transcripts — Knowlez indexes the rest.' }) as string}
            badge="↑"
          />
          <ActionCard
            to={`/app/workspace/${wsId}/connectors`}
            label={t('nav.connectors', { defaultValue: 'Connectors' }) as string}
            body={t('workspace.quick.connectors', { defaultValue: 'Hook up Drive, M365, Slack, kintone, Chatwork…' }) as string}
            badge="⇄"
          />
          <ActionCard
            to={`/app/workspace/${wsId}/compose`}
            label={t('compose.title', { defaultValue: 'Compose' }) as string}
            body={t('workspace.quick.compose', { defaultValue: 'Draft a memo, brief or report grounded in your sources.' }) as string}
            badge="✎"
          />
          <ActionCard
            to={`/app/workspace/${wsId}/compare-docs`}
            label={t('compare_agent.title', { defaultValue: 'Compare' }) as string}
            body={t('workspace.quick.compare', { defaultValue: 'Side-by-side document comparison with citations.' }) as string}
            badge="⇆"
          />
          <ActionCard
            to={`/app/workspace/${wsId}/signals`}
            label={t('signals.title', { defaultValue: 'Signals' }) as string}
            body={t('workspace.quick.signals', { defaultValue: 'Watch for change. Get notified when context shifts.' }) as string}
            badge="◉"
          />
          <ActionCard
            to={`/app/workspace/${wsId}/docs`}
            label={t('nav.docs', { defaultValue: 'Documents' }) as string}
            body={t('workspace.quick.docs', { defaultValue: 'Browse, search inside, re-index or delete your indexed corpus.' }) as string}
            badge="▤"
          />
          <ActionCard
            to={`/app/workspace/${wsId}/search`}
            label={t('nav.search', { defaultValue: 'Search' }) as string}
            body={t('workspace.quick.search', { defaultValue: 'Ranked passage search across every source, with provenance.' }) as string}
            badge="⌕"
          />
          <ActionCard
            to={`/app/workspace/${wsId}/results`}
            label={t('ai_results.title', { defaultValue: 'AI Results' }) as string}
            body={t('workspace.quick.results', { defaultValue: 'Reopen every answer, draft and brief you saved.' }) as string}
            badge="✦"
          />
        </div>
      </section>

      <section>
        <Link
          to={`/app/workspace/${wsId}/ask`}
          className="paper-spotlight rounded-2xl p-7 flex flex-wrap items-center justify-between gap-4 hover:shadow-card transition group"
        >
          <div>
            <h3 className="display text-xl font-semibold text-ink-900">
              {t('workspace.cta.heading', { defaultValue: 'Ready to ask?' })}
            </h3>
            <p className="mt-1 text-[13.5px] text-ink-600">
              {t('workspace.cta.sub', { defaultValue: 'One question. Every chunk that answered it.' })}
            </p>
          </div>
          <span className="btn-press inline-flex items-center px-5 py-2.5 text-sm group-hover:translate-x-0.5 transition-transform">
            {t('workspace.cta.go', { defaultValue: 'Open Ask' })} →
          </span>
        </Link>
      </section>
    </AppShell>
  );
}

function Stat({
  label, value, accent, danger, to,
}: { label: string; value: number; accent?: boolean; danger?: boolean; to?: string }) {
  const inner = (
    <>
      <div className="text-[11px] uppercase tracking-[0.12em] text-ink-500 font-semibold mb-1">{label}</div>
      <div
        className={`num text-2xl font-semibold tabular-nums ${
          danger ? 'text-warn-600' : accent ? 'gradient-text' : 'text-ink-900'
        }`}
      >
        {value}
      </div>
    </>
  );
  if (to) {
    return (
      <Link to={to} className="card-paper p-4 block hover:shadow-card hover:-translate-y-0.5 transition-all">
        {inner}
      </Link>
    );
  }
  return <div className="card-paper p-4">{inner}</div>;
}

function ActionCard({
  to, label, body, badge,
}: { to: string; label: string; body: string; badge: string }) {
  return (
    <Link
      to={to}
      className="card-paper p-5 flex flex-col gap-2 hover:shadow-card hover:-translate-y-0.5 transition-all"
    >
      <div className="flex items-center gap-2">
        <span className="hanko">{badge}</span>
        <span className="display text-[14.5px] font-semibold text-ink-900">{label}</span>
      </div>
      <p className="text-[13px] text-ink-600 leading-relaxed">{body}</p>
    </Link>
  );
}
